import React, { useEffect, useState } from "react";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import { Building2, Plus, Trash2, Edit2, Search } from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";

export default function SchoolDirectoryPage() {
  const { user, customRole } = useAuth();
  const [schools, setSchools] = useState([]);
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("");
  const [search, setSearch] = useState("");
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", city: "", principal_name: "", principal_email: "", contact_phone: "", address: "", notes: "" });

  const isPM = user.role === "project_manager";

  const load = async () => {
    // PM is auto-filtered by their city server-side
    const params = city && !isPM ? `?city=${encodeURIComponent(city)}` : "";
    const [s, c] = await Promise.all([api.get(`/schools${params}`), api.get("/cities")]);
    setSchools(s.data); setCities(c.data);
  };
  useEffect(() => { load(); }, [city]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/schools", form);
      toast.success("School added");
      setShow(false);
      setForm({ name: "", city: "", principal_name: "", principal_email: "", contact_phone: "", address: "", notes: "" });
      load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Failed"); }
  };

  const del = async (s) => {
    if (!window.confirm("Delete this school?")) return;
    await api.delete(`/schools/${s.school_id}`);
    load();
  };

  const filtered = schools.filter((s) => !search || s.name?.toLowerCase().includes(search.toLowerCase()) || s.city?.toLowerCase().includes(search.toLowerCase()));

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <PageHeader title="School Directory" subtitle={isPM ? `Schools in ${user.city || "your city"}` : "All-India school directory — added by Admin & Regional PMs"} icon={Building2}
          actions={!isPM && <button onClick={() => setShow(true)} className="btn-primary"><Plus className="w-4 h-4" /> Add school</button>} />

        <div className="flex flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200">
            <Search className="w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="bg-transparent outline-none text-sm" data-testid="schools-search" />
          </div>
          {!isPM && (
            <select value={city} onChange={(e) => setCity(e.target.value)} className="input-pill py-2 max-w-xs" data-testid="schools-city-filter">
              <option value="">All cities</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.length === 0 && <div className="col-span-full dashboard-card p-12 text-center text-slate-400">No schools found.</div>}
          {filtered.map((s) => (
            <div key={s.school_id} className="dashboard-card p-5" data-testid={`school-${s.school_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display font-bold text-lg">{s.name}</div>
                  <div className="text-xs text-slate-500">{s.city}</div>
                </div>
                {!isPM && <button onClick={() => del(s)} className="text-rose-500"><Trash2 className="w-4 h-4" /></button>}
              </div>
              <div className="text-sm mt-3 space-y-0.5 text-slate-600">
                {s.principal_name && <div>👤 {s.principal_name}</div>}
                {s.principal_email && <div className="text-orange-600">{s.principal_email}</div>}
                {s.contact_phone && <div>📞 {s.contact_phone}</div>}
                {s.address && <div className="text-xs text-slate-500">{s.address}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto relative">
            <ModalCloseBtn onClose={() => setShow(false)} />
            <h3 className="font-display font-black text-2xl mb-4">Add School</h3>
            <form onSubmit={add} className="space-y-3">
              <input required placeholder="School name" className="input-pill" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="school-name" />
              <select required className="input-pill" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="school-city">
                <option value="">Select city</option>
                {cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input placeholder="Principal name" className="input-pill" value={form.principal_name} onChange={(e) => setForm({ ...form, principal_name: e.target.value })} />
              <input type="email" placeholder="Principal email" className="input-pill" value={form.principal_email} onChange={(e) => setForm({ ...form, principal_email: e.target.value })} />
              <input placeholder="Phone" className="input-pill" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
              <input placeholder="Address" className="input-pill" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              <textarea rows={2} placeholder="Notes" className="input-pill" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              <button type="submit" className="btn-primary w-full py-3">Add</button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
